
import { Link } from "react-router-dom";
import { motion } from "framer-motion";

import Navbar from "@/components/site/Navbar";
import Footer from "@/components/site/Footer";

const nextSteps = [
  {
    number: "1",
    title: "We complete your final setup",
    description:
      "We’ll confirm your activation, complete your restaurant setup, and prepare your secure, easy-to-use TapToTable Dashboard access.",
  },
  {
    number: "2",
    title: "Your TapToTable Page goes live",
    description:
      "We’ll complete a final review of your restaurant information, links, selected Promotion, Event, or Featured Item, and customer experience before making your page available to customers.",
  },
  {
    number: "3",
    title: "You receive Dashboard access",
    description:
      "We’ll email your secure, easy-to-use TapToTable Dashboard access so you can create or update Promotions, Events, and Featured Items, manage restaurant details, change photos and links, manage customer actions, and monitor supported customer activity.",
  },
];

const includedItems = [
  "Your hosted TapToTable Page",
  "Secure, Easy-to-Use TapToTable Dashboard",
  "Create and update Promotions, Events, and Featured Items anytime",
  "Update restaurant details, photos, links, and customer actions",
  "Customer activity and supported analytics",
  "Restaurant photo hosting + TapToTable hosting",
  "Ongoing support",
  "Cancel anytime",
];

export default function ActivationSuccess() {
  const supportSubject = encodeURIComponent(
    "TapToTable Activation Support"
  );

  const supportBody = encodeURIComponent(
    "Hello TapToTable,\n\nI recently activated my TapToTable Page and need assistance.\n\nRestaurant name:\n\nEmail used during checkout:\n\nHow can you help?\n\n"
  );

  const supportEmailUrl =
    "mailto:support@taptotable.co" +
    `?subject=${supportSubject}` +
    `&body=${supportBody}`;

  const additionalLocationsSubject = encodeURIComponent(
    "TapToTable Additional Restaurant Locations"
  );

  const additionalLocationsBody = encodeURIComponent(
    "Hello TapToTable,\n\nI would like information about adding restaurant locations under the same ownership.\n\nRestaurant name:\n\nEmail used during checkout:\n\nAdditional locations requested:\n\n"
  );

  const additionalLocationsEmailUrl =
    "mailto:support@taptotable.co" +
    `?subject=${additionalLocationsSubject}` +
    `&body=${additionalLocationsBody}`;

  return (
    <div
      className="min-h-screen bg-[#09090B] text-white"
      data-testid="activation-success-page"
    >
      <Navbar />

      <main className="px-6 pb-24 pt-32 md:px-12">
        <div className="mx-auto max-w-5xl">
          <motion.section
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.55 }}
            className="text-center"
          >
            <div className="mx-auto inline-flex h-16 w-16 items-center justify-center rounded-full border border-[#00E5FF]/40 bg-[#00E5FF]/15 text-3xl font-bold text-[#00E5FF]">
              ✓
            </div>

            <div className="mt-6 text-[11px] font-bold uppercase tracking-[0.22em] text-[#00E5FF]">
              Welcome to TapToTable
            </div>

            <h1
              className="mx-auto mt-4 max-w-4xl font-display text-4xl font-semibold leading-[1.08] tracking-tight sm:text-5xl lg:text-6xl"
              data-testid="activation-success-heading"
            >
              Your TapToTable Activation Is Confirmed
            </h1>

            <p className="mx-auto mt-6 max-w-2xl text-base leading-relaxed text-zinc-400 md:text-lg">
              Thank you for choosing TapToTable. Your activation is confirmed,
              and we&apos;re completing the final setup for your restaurant.
              We&apos;ll email your secure, easy-to-use TapToTable Dashboard
              access and let you know as soon as your TapToTable Page is
              ready for customers.
            </p>
          </motion.section>

          <motion.section
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.55, delay: 0.1 }}
            className="mt-12 overflow-hidden rounded-3xl border border-[#00E5FF]/30 bg-[#00E5FF]/10"
          >
            <div className="grid lg:grid-cols-12">
              <div className="border-b border-[#00E5FF]/20 p-6 md:p-8 lg:col-span-7 lg:border-b-0 lg:border-r">
                <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#00E5FF]">
                  What Happens Next
                </div>

                <h2 className="mt-3 font-display text-3xl font-semibold">
                  We&apos;ll Take It From Here
                </h2>

                <p className="mt-4 max-w-xl text-sm leading-relaxed text-zinc-300 md:text-base">
                  You&apos;re all set. We&apos;ll finish preparing your
                  TapToTable Page and make sure everything is ready before
                  sending your secure, easy-to-use TapToTable Dashboard access.
                </p>

                <div className="mt-7 space-y-6">
                  {nextSteps.map((step) => (
                    <div
                      key={step.number}
                      className="flex items-start gap-4"
                    >
                      <span className="inline-flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-[#00E5FF] text-sm font-bold text-black">
                        {step.number}
                      </span>

                      <div>
                        <h3 className="font-display text-lg font-semibold text-white">
                          {step.title}
                        </h3>

                        <p className="mt-1 text-sm leading-relaxed text-zinc-400">
                          {step.description}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="p-6 md:p-8 lg:col-span-5">
                <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#00E5FF]">
                  Your TapToTable Plan Includes
                </div>

                <h2 className="mt-3 font-display text-2xl font-semibold">
                  Everything You Need to Keep Your Page Current
                </h2>

                <ul className="mt-6 space-y-4">
                  {includedItems.map((item) => (
                    <li
                      key={item}
                      className="flex items-start gap-3 text-sm leading-relaxed text-zinc-300"
                    >
                      <span className="mt-0.5 inline-flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full bg-[#00E5FF]/15 text-xs text-[#00E5FF]">
                        ✓
                      </span>
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>

                <div className="mt-7 rounded-2xl border border-white/10 bg-black/20 p-5">
                  <div className="text-sm font-semibold text-white">
                    Watch your email
                  </div>

                  <p className="mt-2 text-xs leading-relaxed text-zinc-400">
                    We&apos;ll send your secure, easy-to-use TapToTable Dashboard
                    access and any important setup information to the email
                    address you used during checkout. Please allow a little
                    time for final setup and check your spam or promotions
                    folder if needed.
                  </p>
                </div>
              </div>
            </div>
          </motion.section>

          <motion.section
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.55, delay: 0.18 }}
            className="mt-6 rounded-3xl border border-violet-400/20 bg-violet-400/[0.05] p-6 md:p-8"
          >
            <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-violet-300">
              Your Dashboard
            </div>

            <h2 className="mt-3 font-display text-2xl font-semibold">
              Promote What Matters Right Now
            </h2>

            <p className="mt-3 max-w-3xl text-sm leading-relaxed text-zinc-400 md:text-base">
              Use your secure, easy-to-use TapToTable Dashboard on your
              phone or computer to switch between Promotions, Events,
              and Featured Items. Create special offers, promote live
              music, tastings, watch parties, and holiday events, or
              showcase a signature menu item without requiring a
              discount or claim code.
            </p>
          </motion.section>

          <motion.section
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.55, delay: 0.22 }}
            className="mt-6 grid gap-6 md:grid-cols-2"
          >
            <div className="rounded-3xl border border-white/10 bg-[#121214] p-6">
              <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-500">
                Need Assistance?
              </div>

              <h2 className="mt-3 font-display text-xl font-semibold">
                Questions? We&apos;re Here to Help.
              </h2>

              <p className="mt-3 text-sm leading-relaxed text-zinc-400">
                Support is included. Include your restaurant name and
                the email address used during checkout so we can locate
                your activation quickly.
              </p>

              <a
                href={supportEmailUrl}
                className="mt-5 inline-flex w-full items-center justify-center rounded-xl border border-white/10 bg-white/5 px-4 py-3 text-sm font-semibold text-white transition-colors hover:bg-white/10"
                data-testid="activation-support-button"
              >
                Email TapToTable Support
              </a>
            </div>

            <div className="rounded-3xl border border-white/10 bg-[#121214] p-6">
              <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-500">
                Continue Exploring
              </div>

              <h2 className="mt-3 font-display text-xl font-semibold">
                Return to TapToTable
              </h2>

              <p className="mt-3 text-sm leading-relaxed text-zinc-400">
                Learn more about how TapToTable helps turn online interest
                into restaurant visits through Promotions, Events,
                Featured Items, and clear customer actions.
              </p>

              <Link
                to="/"
                className="mt-5 inline-flex w-full items-center justify-center rounded-xl bg-[#00E5FF] px-4 py-3 text-sm font-semibold text-black transition-colors hover:bg-[#66FFFF]"
                data-testid="return-home-button"
              >
                Return to TapToTable
              </Link>
            </div>
          </motion.section>

          <motion.section
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: 0.25 }}
            className="mt-6 rounded-2xl border border-white/10 bg-[#121214] p-6"
          >
            <h2 className="font-display text-lg font-semibold text-white">
              Own Additional Restaurant Locations?
            </h2>

            <p className="mt-3 text-sm leading-relaxed text-zinc-400">
              Your first restaurant location is $59/month. Each additional
              eligible location under the same ownership is $39/month,
              with its own TapToTable Page, login, dashboard, and
              separately arranged subscription.
            </p>

            <p className="mt-2 text-xs leading-relaxed text-zinc-500">
              Additional locations are not automatically activated or
              charged as part of your first-location subscription.
              Contact us to arrange them after ownership is confirmed.
            </p>

            <a
              href={additionalLocationsEmailUrl}
              className="mt-4 inline-flex items-center justify-center rounded-xl border border-[#00E5FF]/30 bg-[#00E5FF]/10 px-4 py-3 text-sm font-semibold text-[#66FFFF] transition-colors hover:bg-[#00E5FF]/15"
            >
              Ask About Additional Locations
            </a>
          </motion.section>

          <div className="mx-auto mt-8 max-w-2xl text-center">
            <p className="text-xs leading-relaxed text-zinc-500">
              Your first-location TapToTable plan is billed at $59 per month
              through Stripe and automatically renews each month until
              canceled. Any additional restaurant subscriptions are
              arranged and billed separately.
            </p>

            <p className="mt-3 text-sm text-zinc-400">
              Thanks for choosing TapToTable. We look forward to helping
              your restaurant turn more online interest into visits and
              customer activity.
            </p>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
